import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bug, Bell } from 'lucide-react';
import Dialog from './ui/Dialog';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { isPushSupported, isPushSubscribed, subscribeToPush, watchPushState } from '../services/pushNotifications';
import { openBugReport } from './BugReportButton';

/** The key of the one-time message (the server remembers it per user: user_announcements_seen). */
export const WHATS_NEW_KEY = 'whats_new_reports_2026_11';

/**
 * D-341 (4): a one-time "what's new" message on the first visit after the update - you can now report bugs and
 * suggest improvements, where the button is, and where "הדיווחים שלי" is. Remembered PER USER on the server (any
 * device sees it once). If browser notifications are off, the same message also asks to turn them on.
 */
export default function WhatsNew() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const isStaff = role === 'admin' || role === 'agent';
  const [closed, setClosed] = useState(false);
  const [pushOff, setPushOff] = useState(false);

  const { data } = useQuery({
    queryKey: ['announcement', WHATS_NEW_KEY],
    queryFn: () => api.getAnnouncement(WHATS_NEW_KEY),
    enabled: isStaff,
    staleTime: Infinity,
    retry: false,
  });
  const unseen = isStaff && data?.data?.seen === false && !closed;

  useEffect(() => {
    if (!unseen) return;
    let cancelled = false;
    const look = () => (async () => {
      if (!isPushSupported()) return;
      const on = Notification.permission === 'granted' ? await isPushSubscribed() : false;
      if (!cancelled) setPushOff(!on);
    })().catch(() => undefined);
    void look();
    // FIX-PUSH-REAL: turned on in the bar / the profile / another window -> the line goes away.
    const stop = watchPushState(() => { void look(); });
    return () => { cancelled = true; stop(); };
  }, [unseen]);

  if (!unseen) return null;

  const markSeen = () => {
    setClosed(true);
    qc.setQueryData(['announcement', WHATS_NEW_KEY], { success: true, data: { key: WHATS_NEW_KEY, seen: true } });
    void api.markAnnouncementSeen(WHATS_NEW_KEY).catch(() => undefined);
  };
  const tryNow = () => {
    markSeen();
    // let the message finish closing first, so the screenshot does not show it
    window.setTimeout(() => openBugReport(), 50);
  };

  return (
    <Dialog
      open
      onClose={markSeen}
      title={t('whatsNew.title')}
      size="md"
      footer={(
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={markSeen} data-testid="whatsnew-ok">{t('whatsNew.ok')}</button>
          <button type="button" className="btn btn-primary" onClick={tryNow} data-testid="whatsnew-try">
            <Bug size={16} aria-hidden="true" /> {t('whatsNew.tryNow')}
          </button>
        </div>
      )}
    >
      <div className="space-y-3 text-sm text-text-1" data-testid="whatsnew">
        <p>{t('whatsNew.body')}</p>
        {pushOff && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-warning-900" data-testid="whatsnew-push">
            <Bell size={16} aria-hidden="true" />
            <span className="flex-1">{t('whatsNew.enablePush')}</span>
            <button type="button" className="btn btn-secondary text-xs py-1 px-3" onClick={() => { void subscribeToPush().then((ok) => { if (ok) setPushOff(false); }); }}>
              {t('push.allow')}
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
